import { useMutation, useQuery, useQueryClient } from "react-query";
import type { UseMutationResult, UseQueryResult } from "react-query";
import config from "../../config";
import { searchTypes, searchApiTypes } from "../../modules/aml/aml.constant";
import type {
  AMLBusinessSearchRequest,
  AMLIndividualSearchRequest,
  AddAmlSearchResponse,
  AmlAlertUpdatePayload,
  AmlAlertsResponse,
  AmlBusinessHitsResponse,
  AmlDetailsResponse,
  AmlHitRecentChangesResponse,
  AmlIndividualHitsResponse,
  AmlScreenHitsResponse,
  AmlScreenHitsUpdatePayload,
  AmlSearchBulkLinkRequest,
  AmlSearchResponse,
  AsyncJobResponse,
  BulkScreeningStatusResponse,
  BusinessIndividualKeyPartyUboAMLSearchResponse,
  DownloadReportTypes,
  GetKeyPartySearchesQueryParams,
  KYCAlertUpdateRequest,
  KycAlertsResponse,
  LinkAmlSearchesToProfilePayload,
  LinkAmlSearchesToProfileResponse,
  LinkKeyPartyUboAmlSearchesToProfilePayload,
  OpenAmlAlertsResponse,
  ProfileAmlScreenSearchResponse,
  ProfileKYCAlertById,
} from "../../modules/aml/types/aml.type";
import { AsyncJobStatus } from "../../modules/aml/types/aml.type";
import {
  apiVersion,
  ProfileTabsTypes,
} from "../../shared/constants/common.constant";
import type { Pagination, Paginator } from "../../shared/types/table.type";
import { createQueryString } from "../../utils";
import { getAsync, postAsync, putAsync } from "../protect-api";
import {
  getAmlAlertApiUrl,
  getAmlSearchApiUrl,
  getAmlSearchHitApiUrl,
} from "./utils";
import type { PreSignedURLResponse } from "../../shared/types/common.type";
import type { AmlSearchesType } from "../../modules/profiles/types/amlScreen.type";

export const AML_INDIVIDUAL_DETAILS_KEY = "aml-individual-details";
export const AML_BUSINESS_DETAILS_KEY = "aml-business-details";
export const AML_SEARCH_RESULTS = "aml-search-results";
export const AML_DETAILS_HITS_KEY = "aml-hits-details";
export const AML_SCREEN_KEY_PARTIES_KEY = "aml-screen-key-parties-details";
export const AML_ALERTS = "aml-alerts";
export const OPEN_AML_ALERTS = "open-aml-alerts";
export const KYC_ALERTS = "kyc-alerts";
export const BULK_SCREENING_STATUS = "bulk-screening-status";
export const DOWNLOAD_AML_REPORT = "download-aml-report";
export const AML_INDIVIDUAL_DETAILS_SUPPLIER_KEY = "aml-individual-details";
export const AML_BUSINESS_DETAILS_SUPPLIER_KEY = "aml-business-details";
export const AML_HIT_CHANGES_KEY = "aml-hit-changes";
export const AML_SEARCHES_KEY = "aml-searches";

const getAmlBusinessDetails = async (
  searchId: string,
  hitId: string
): Promise<AmlDetailsResponse> => {
  const { data } = await getAsync<AmlDetailsResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/businesses/${searchId}/hits/${hitId}`
  );

  return data;
};

export const useGetAmlBusinessDetails = (
  searchId: string,
  hitId: string
): ReturnType<typeof useQuery<AmlDetailsResponse>> => {
  return useQuery<AmlDetailsResponse>([AML_BUSINESS_DETAILS_KEY], () =>
    getAmlBusinessDetails(searchId, hitId)
  );
};

const getAmlBusinessDetailsBySupplierHitId = async (
  supplierHitId: string
): Promise<AmlDetailsResponse> => {
  const { data } = await getAsync<AmlDetailsResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searchLinks/businesses/${supplierHitId}/`
  );

  return data;
};

export const useGetAmlBusinessDetailsBySupplierHitId = (
  supplierHitId: string
): ReturnType<typeof useQuery<AmlDetailsResponse>> => {
  return useQuery<AmlDetailsResponse>([AML_BUSINESS_DETAILS_SUPPLIER_KEY], () =>
    getAmlBusinessDetailsBySupplierHitId(supplierHitId)
  );
};

const getAmlIndividualDetails = async (
  searchId: string,
  hitId: string
): Promise<AmlDetailsResponse> => {
  const { data } = await getAsync<AmlDetailsResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/individuals/${searchId}/hits/${hitId}`
  );

  return data;
};

export const useGetAmlIndividualDetails = (
  searchId: string,
  hitId: string
): ReturnType<typeof useQuery<AmlDetailsResponse>> => {
  return useQuery<AmlDetailsResponse>([AML_INDIVIDUAL_DETAILS_KEY], () =>
    getAmlIndividualDetails(searchId, hitId)
  );
};

const getAmlIndividualDetailsBySupplierHitId = async (
  supplierHitId: string
): Promise<AmlDetailsResponse> => {
  const { data } = await getAsync<AmlDetailsResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searchLinks/individuals/${supplierHitId}`
  );

  return data;
};

export const useGetAmlIndividualDetailsBySupplierHitId = (
  supplierHitId: string
): UseQueryResult<AmlDetailsResponse> => {
  return useQuery<AmlDetailsResponse>(
    [AML_INDIVIDUAL_DETAILS_SUPPLIER_KEY],
    () => getAmlIndividualDetailsBySupplierHitId(supplierHitId)
  );
};

const getAmlSearchHits = async (
  searchId: string,
  type: string,
  selectedTab: string,
  query: Pagination
): Promise<AmlIndividualHitsResponse | AmlBusinessHitsResponse> => {
  const response = await getAsync<
    AmlIndividualHitsResponse | AmlBusinessHitsResponse
  >(getAmlSearchHitApiUrl(searchId, selectedTab, type, query));
  return response.data;
};

export const useGetAmlSearchHits = (
  searchId: string,
  type: string,
  selectedTab: string,
  query: Pagination
): UseQueryResult<AmlIndividualHitsResponse | AmlBusinessHitsResponse> => {
  return useQuery<AmlIndividualHitsResponse | AmlBusinessHitsResponse>(
    [AML_DETAILS_HITS_KEY + searchId, query],
    () => getAmlSearchHits(searchId, type, selectedTab, query)
  );
};

export const getAmlSearchResults = async ({
  searchId,
  searchType,
  page,
}: {
  searchId: string;
  searchType: string;
  page: number;
}): Promise<AmlSearchResponse> => {
  searchType =
    searchType + (searchType === searchTypes.individual ? "s" : "es");

  const { data } = await getAsync<AmlSearchResponse>(
    `${
      config.COMPLIANCE_API_URL
    }/${apiVersion}/searches/${searchType}/${searchId}/hits?${createQueryString(
      {
        page,
        limit: 10,
      }
    )}`
  );

  return data;
};

export const useAmlSearchResults = (): UseMutationResult<
  AmlSearchResponse,
  unknown,
  { searchId: string; searchType: string; page: number },
  unknown
> => {
  return useMutation(getAmlSearchResults);
};

const addAmlSearch = async ({
  searchPayload,
  searchType,
}: {
  searchPayload: AMLIndividualSearchRequest | AMLBusinessSearchRequest;
  searchType: string;
}): Promise<AddAmlSearchResponse> => {
  searchType =
    searchType + (searchType === searchTypes.individual ? "s" : "es");

  const { data } = await postAsync<AddAmlSearchResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}`,
    searchPayload
  );

  return data;
};

export const useAddAmlSearch = (): UseMutationResult<
  AddAmlSearchResponse,
  unknown,
  {
    searchPayload: AMLIndividualSearchRequest | AMLBusinessSearchRequest;
    searchType: string;
  },
  unknown
> => {
  return useMutation(addAmlSearch);
};

export const getAmlResults = async ({
  selectedTab,
  profileId,
  page,
  type,
  keyPartyTypes,
}: {
  selectedTab: string;
  profileId: string;
  page: number;
  type?: string;
  keyPartyTypes?: string[];
}): Promise<ProfileAmlScreenSearchResponse> => {
  const queryParamsPayload: GetKeyPartySearchesQueryParams = {
    page,
    type,
    limit: 10,
  };
  if (
    selectedTab === ProfileTabsTypes.keyParties ||
    selectedTab === ProfileTabsTypes.ubos
  ) {
    queryParamsPayload.keyPartyTypes = keyPartyTypes;
  }
  const { data } = await getAsync<ProfileAmlScreenSearchResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/${getAmlSearchApiUrl(
      profileId,
      selectedTab
    )}?${createQueryString(queryParamsPayload)}`
  );

  return data;
};

export const useGetAmlResults = (
  selectedTab: string,
  profileId: string,
  page: number,
  type?: string,
  keyPartyTypes?: string[]
): UseQueryResult<ProfileAmlScreenSearchResponse> => {
  return useQuery(
    [AML_SEARCH_RESULTS, profileId, selectedTab, page, type, keyPartyTypes],
    () => getAmlResults({ selectedTab, profileId, page, type, keyPartyTypes })
  );
};

const postLinkAmlSearchesToProfile = async ({
  profileId,
  payload,
}: {
  profileId: string;
  payload: LinkAmlSearchesToProfilePayload;
}): Promise<LinkAmlSearchesToProfileResponse> => {
  const { data } = await postAsync<LinkAmlSearchesToProfileResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/searches/link`,
    payload
  );

  return data;
};

const postLinkKeyPartiesUboAmlSearchesToProfile = async ({
  profileId,
  payload,
}: {
  profileId: string;
  payload: LinkKeyPartyUboAmlSearchesToProfilePayload;
}): Promise<LinkAmlSearchesToProfileResponse> => {
  const { data } = await postAsync<LinkAmlSearchesToProfileResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/searches/link`,
    payload
  );

  return data;
};

const businessSearch = async ({
  payload,
}: {
  payload: AMLBusinessSearchRequest | AMLIndividualSearchRequest;
}): Promise<BusinessIndividualKeyPartyUboAMLSearchResponse> => {
  const { data } =
    await postAsync<BusinessIndividualKeyPartyUboAMLSearchResponse>(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/businesses`,
      payload
    );

  return data;
};

const individualSearch = async ({
  payload,
}: {
  payload: AMLBusinessSearchRequest | AMLIndividualSearchRequest;
}): Promise<BusinessIndividualKeyPartyUboAMLSearchResponse> => {
  const { data } =
    await postAsync<BusinessIndividualKeyPartyUboAMLSearchResponse>(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/individuals`,
      payload
    );

  return data;
};

export const useLinkAmlSearchesToProfile = (): UseMutationResult<
  LinkAmlSearchesToProfileResponse,
  unknown,
  { profileId: string; payload: LinkAmlSearchesToProfilePayload },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(postLinkAmlSearchesToProfile, {
    onSettled: async () => {
      queryClient.invalidateQueries();
    },
  });
};

export const useLinkKeyPartiesUboAmlSearchesToProfile = (): UseMutationResult<
  LinkAmlSearchesToProfileResponse,
  unknown,
  { profileId: string; payload: LinkKeyPartyUboAmlSearchesToProfilePayload },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(postLinkKeyPartiesUboAmlSearchesToProfile, {
    onSettled: async () => {
      queryClient.invalidateQueries();
    },
  });
};

export const useAmlBusinessSearch = (): UseMutationResult<
  BusinessIndividualKeyPartyUboAMLSearchResponse,
  unknown,
  { payload: AMLIndividualSearchRequest | AMLBusinessSearchRequest },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(businessSearch, {
    onSettled: async () => {
      queryClient.invalidateQueries();
    },
  });
};

export const useAmlIndividualSearch = (): UseMutationResult<
  BusinessIndividualKeyPartyUboAMLSearchResponse,
  unknown,
  { payload: AMLIndividualSearchRequest | AMLBusinessSearchRequest },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(individualSearch, {
    onSettled: async () => {
      queryClient.invalidateQueries();
    },
  });
};

const getAmlAlerts = async ({
  profileId,
  paginator,
}: {
  profileId: string;
  paginator: Paginator;
}): Promise<AmlAlertsResponse> => {
  const { data } = await getAsync<AmlAlertsResponse>(
    `${
      config.COMPLIANCE_API_URL
    }/${apiVersion}/profiles/${profileId}/hits?${createQueryString({
      page: paginator.page,
      limit: paginator.limit,
      HitDecisions: paginator.HitDecisions ?? ["undecided"],
    })}`
  );

  return data;
};

const getOpenAmlAlerts = async ({
  profileId,
}: {
  profileId: string;
}): Promise<OpenAmlAlertsResponse> => {
  const { data } = await getAsync<OpenAmlAlertsResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}`
  );

  return data;
};

export const useGetAmlAlerts = (
  profileId: string,
  paginator: Paginator
): UseQueryResult<AmlAlertsResponse> => {
  return useQuery([AML_ALERTS, profileId, paginator], () =>
    getAmlAlerts({ profileId, paginator })
  );
};

export const useGetOpenAmlAlerts = (
  profileId: string
): UseQueryResult<OpenAmlAlertsResponse> => {
  return useQuery([AML_ALERTS, profileId], () =>
    getOpenAmlAlerts({ profileId })
  );
};

const getKycAlerts = async ({
  profileId,
  paginator,
  statuses,
}: {
  profileId: string;
  paginator: Paginator;
  statuses: string[];
}): Promise<KycAlertsResponse> => {
  const { data } = await getAsync<KycAlertsResponse>(
    `${
      config.COMPLIANCE_API_URL
    }/${apiVersion}/kycMonitoring/profiles/${profileId}/alerts?${createQueryString(
      {
        page: paginator.page,
        limit: paginator.limit,
        statuses,
      }
    )}`
  );

  return data;
};

const updateAmlSearchHits = async ({
  searchId,
  payload,
  selectedTab,
  hitProfileType,
}: {
  searchId: string;
  payload: AmlScreenHitsUpdatePayload;
  selectedTab: string;
  hitProfileType: string;
}): Promise<AmlScreenHitsResponse> => {
  const { data } = await putAsync<AmlScreenHitsResponse>(
    getAmlSearchHitApiUrl(searchId, selectedTab, hitProfileType, {}),
    payload
  );

  return data;
};

export const useUpdateAmlSearchHits = (): UseMutationResult<
  AmlScreenHitsResponse,
  unknown,
  {
    searchId: string;
    payload: AmlScreenHitsUpdatePayload;
    selectedTab: string;
    hitProfileType: string;
  },
  unknown
> => {
  return useMutation(updateAmlSearchHits);
};

export const useGetKycAlerts = (
  profileId: string,
  paginator: Paginator,
  statuses: string[]
): UseQueryResult<KycAlertsResponse> => {
  return useQuery([KYC_ALERTS, profileId, paginator], () =>
    getKycAlerts({ profileId, paginator, statuses })
  );
};

const updateAmlAlertHit = async ({
  searchId,
  hitId,
  payload,
  profileType,
}: {
  searchId: string;
  hitId: string;
  payload: AmlAlertUpdatePayload;
  profileType: string;
}): Promise<AmlScreenHitsResponse> => {
  const { data } = await putAsync<AmlScreenHitsResponse>(
    getAmlAlertApiUrl(searchId, hitId, profileType),
    payload
  );

  return data;
};

export const useUpdateAmlAlertHit = (): UseMutationResult<
  AmlScreenHitsResponse,
  unknown,
  {
    searchId: string;
    hitId: string;
    payload: AmlAlertUpdatePayload;
    profileType: string;
  },
  unknown
> => {
  return useMutation(updateAmlAlertHit);
};

export const useBulkLinkAmlSearchesToProfile = (): UseMutationResult<
  AsyncJobResponse,
  unknown,
  { profileId: string; payload: AmlSearchBulkLinkRequest },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(postBulkLinkAmlSearchesToProfile, {
    onSettled: async () => {
      queryClient.invalidateQueries();
    },
  });
};

const postBulkLinkAmlSearchesToProfile = async ({
  profileId,
  payload,
}: {
  profileId: string;
  payload: AmlSearchBulkLinkRequest;
}): Promise<AsyncJobResponse> => {
  const { data } = await postAsync<AsyncJobResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/searches/bulk`,
    payload
  );

  return data;
};

const getAsyncAmlJobs = async ({
  targetType,
  statuses,
  paginator,
}: {
  targetType: string;
  statuses: string[];
  paginator: Paginator;
}): Promise<BulkScreeningStatusResponse> => {
  const { data } = await getAsync<BulkScreeningStatusResponse>(
    `${
      config.COMPLIANCE_API_URL
    }/${apiVersion}/asyncAmlJobs?${createQueryString({
      page: paginator.page,
      limit: paginator.limit,
      targetType,
      statuses,
    })}`
  );

  return data;
};

export const useGetAsyncAmlJobs = (
  targetType: string,
  statuses: string[],
  paginator: Paginator
): UseQueryResult<BulkScreeningStatusResponse> => {
  return useQuery(
    [BULK_SCREENING_STATUS, targetType, statuses, paginator],
    () => getAsyncAmlJobs({ targetType, statuses, paginator })
  );
};

export const useBulkLinkAmlSearchesToKeyParties = (): UseMutationResult<
  AsyncJobResponse,
  unknown,
  { profileId: string; payload: AmlSearchBulkLinkRequest },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(postBulkLinkAmlSearchesToKeyParties, {
    onSettled: async () => {
      queryClient.invalidateQueries();
    },
  });
};

const postBulkLinkAmlSearchesToKeyParties = async ({
  profileId,
  payload,
}: {
  profileId: string;
  payload: AmlSearchBulkLinkRequest;
}): Promise<AsyncJobResponse> => {
  const { data } = await postAsync<AsyncJobResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/searches/bulk`,
    payload
  );

  return data;
};

const getProfileOpenAmlAlerts = async ({
  profileId,
  paginator,
}: {
  profileId: string;
  paginator: Paginator;
}): Promise<AmlAlertsResponse> => {
  const { data } = await getAsync<AmlAlertsResponse>(
    `${
      config.COMPLIANCE_API_URL
    }/${apiVersion}/profiles/${profileId}/amlAlerts?${createQueryString({
      page: paginator.page,
      limit: paginator.limit,
      HitDecisions: paginator.HitDecisions ?? ["undecided"],
    })}`
  );

  return data;
};

export const useGetProfileOpenAmlAlerts = (
  profileId: string,
  paginator: Paginator
): UseQueryResult<AmlAlertsResponse> => {
  return useQuery<AmlAlertsResponse>([OPEN_AML_ALERTS, paginator], () =>
    getProfileOpenAmlAlerts({ profileId, paginator })
  );
};

export const getProfileKYCAlertByAlertId = async ({
  profileId,
  alertId,
}: {
  profileId: string;
  alertId: string;
}): Promise<ProfileKYCAlertById> => {
  const { data } = await getAsync<ProfileKYCAlertById>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles/${profileId}/alerts/${alertId}`
  );
  return data;
};

const updateKYCAlert = async ({
  profileId,
  alertId,
  payload,
}: {
  profileId: string;
  alertId: string;
  payload: KYCAlertUpdateRequest;
}): Promise<ProfileKYCAlertById> => {
  const { data } = await putAsync<ProfileKYCAlertById>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles/${profileId}/alerts/${alertId}`,
    payload
  );
  return data;
};

export const useUpdateKYCAlert = (): UseMutationResult<
  ProfileKYCAlertById,
  unknown,
  { profileId: string; alertId: string; payload: KYCAlertUpdateRequest },
  unknown
> => {
  return useMutation(updateKYCAlert);
};

export const getDownloadReport = async (
  searchType: string,
  searchId: string,
  hitIds: string[]
): Promise<{
  downloadUrl: string;
  fileName: string;
  expiresAt: string;
  apiFailed: boolean;
}> => {
  try {
    const { data } = await postAsync<PreSignedURLResponse>(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/download`,
      hitIds
    );

    return {
      downloadUrl: data.downloadUrl,
      fileName: data.fileName,
      expiresAt: data.expiresAt,
      apiFailed: false,
    };
  } catch {
    return {
      downloadUrl: "",
      fileName: "",
      expiresAt: "",
      apiFailed: true,
    };
  }
};

export const useDownloadReport = (
  searchType: DownloadReportTypes,
  searchId: string,
  hitIds: string[]
): UseQueryResult<{
  downloadUrl: string;
  fileName: string;
  expiresAt: string;
  apiFailed: boolean;
}> => {
  return useQuery(
    [DOWNLOAD_AML_REPORT, searchType, searchId],
    () => getDownloadReport(searchType, searchId, hitIds),
    { enabled: false }
  );
};

export const getAsyncJobInfo = async ({
  jobId,
}: {
  jobId: string;
}): Promise<AsyncJobResponse> => {
  const { data } = await getAsync<AsyncJobResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/asyncAmlJobs/${jobId}`
  );
  return data;
};

export const useGetAsyncJobInfo = (): UseMutationResult<
  AsyncJobResponse,
  unknown,
  { jobId: string },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(getAsyncJobInfo, {
    onSuccess: ({ status }) => {
      if (
        status === AsyncJobStatus.processing ||
        status === AsyncJobStatus.completed
      ) {
        queryClient.invalidateQueries(AML_SEARCH_RESULTS);
      }
    },
  });
};

export const getAMLHitDetailReport = async (
  searchType: string,
  searchId: string,
  hitId: string
): Promise<{
  downloadUrl: string;
  fileName: string;
  expiresAt: string;
  apiFailed: boolean;
}> => {
  try {
    const { data } = await getAsync<PreSignedURLResponse>(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/hits/${hitId}/download`
    );
    return {
      downloadUrl: data.downloadUrl,
      fileName: data.fileName,
      expiresAt: data.expiresAt,
      apiFailed: false,
    };
  } catch {
    return {
      downloadUrl: "",
      fileName: "",
      expiresAt: "",
      apiFailed: true,
    };
  }
};

export const getAMLHitCopyrightDetailReport = async (
  searchId: string,
  hitId: string,
  sourceId: string,
  searchType: string
): Promise<{
  downloadUrl: string;
  fileName: string;
  expiresAt: string;
  apiFailed: boolean;
}> => {
  try {
    const { data } = await getAsync<PreSignedURLResponse>(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/hits/${hitId}/sources/${sourceId}/copyrighted/download`
    );
    return {
      downloadUrl: data.downloadUrl,
      fileName: data.fileName,
      expiresAt: data.expiresAt,
      apiFailed: false,
    };
  } catch {
    return {
      downloadUrl: "",
      fileName: "",
      expiresAt: "",
      apiFailed: true,
    };
  }
};

export const getAMLHitDetailsReport = async ({
  searchType,
  supplierHitId,
}: {
  searchType: string;
  supplierHitId: string;
}): Promise<{
  downloadUrl: string;
  fileName: string;
  expiresAt: string;
  apiFailed: boolean;
}> => {
  try {
    const { data } = await postAsync<PreSignedURLResponse>(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searchLinks/${searchType}/${supplierHitId}/download`,
      undefined,
      undefined,
      true
    );

    return {
      downloadUrl: data.downloadUrl,
      fileName: data.fileName,
      expiresAt: data.expiresAt,
      apiFailed: false,
    };
  } catch {
    return {
      downloadUrl: "",
      fileName: "",
      expiresAt: "",
      apiFailed: true,
    };
  }
};

export const useDownloadBusinessHitReport = (): UseMutationResult<
  {
    downloadUrl: string;
    fileName: string;
    expiresAt: string;
    apiFailed: boolean;
  },
  unknown,
  string,
  unknown
> => {
  return useMutation((supplierHitId: string) =>
    getAMLHitDetailsReport({
      searchType: searchApiTypes.business,
      supplierHitId,
    })
  );
};

export const useDownloadIndividualHitReport = (): UseMutationResult<
  {
    downloadUrl: string;
    fileName: string;
    expiresAt: string;
    apiFailed: boolean;
  },
  unknown,
  string,
  unknown
> => {
  return useMutation((supplierHitId: string) =>
    getAMLHitDetailsReport({
      searchType: searchApiTypes.individual,
      supplierHitId,
    })
  );
};

async function getAmlHitRecentChanges({
  searchType,
  searchId,
  hitId,
}: {
  searchType: string;
  searchId: string;
  hitId: string;
}): Promise<AmlHitRecentChangesResponse> {
  searchType =
    searchType === searchTypes.individual
      ? searchApiTypes.individual
      : searchApiTypes.business;

  const { data } = await getAsync<AmlHitRecentChangesResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/hits/${hitId}/changes`,
    undefined,
    undefined,
    true
  );
  return data;
}

export const useGetAmlHitRecentChanges = (
  searchType: string,
  searchId: string,
  hitId: string
): UseQueryResult<AmlHitRecentChangesResponse> => {
  return useQuery<AmlHitRecentChangesResponse>(
    [AML_HIT_CHANGES_KEY, searchType, searchId, hitId],
    () => getAmlHitRecentChanges({ searchType, searchId, hitId })
  );
};

const getAmlSearch = async (
  searchId: string,
  searchType: string
): Promise<AmlSearchesType> => {
  searchType =
    searchType === searchTypes.individual
      ? searchApiTypes.individual
      : searchApiTypes.business;

  const { data } = await getAsync<AmlSearchesType>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}`
  );

  return data;
};

export const useGetAmlSearch = (
  searchId: string,
  searchType: string
): ReturnType<typeof useQuery<AmlSearchesType>> => {
  return useQuery<AmlSearchesType>([AML_SEARCHES_KEY], () =>
    getAmlSearch(searchId, searchType)
  );
};
